import React, {useCallback, useEffect, useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import ConfirmationDialog from "../confirmation-dialog";
import CustomJsonForms from "../custom-json-forms";
import FullscreenDialog from "../fullscreen-dialog";
import MaterialTable from "../material-table";
import SettingsDialog from "../settings-dialog";
import {appSelector} from "../../state/app/appSelectors";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {
  createTheme,
  deleteTheme,
  fetchThemeList,
  fetchThemeSchema,
  getTheme,
  hideTheme,
  hideThemeList,
  showTheme,
  updateTheme
} from "../../state/package-manager/packageManagerActions";
import {themesManagerSelector} from "../../state/package-manager/packageManagerSelectors";
import {localizeI18nObj} from "../../utils/i18n";

const ThemesManager = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {language, languages} = useSelector(appSelector);

  const nodeId = useSelector(nodeIdSelector);
  const {isVisible, themes, schema, uiSchema, themeId, theme} = useSelector(themesManagerSelector);

  const [themeToDelete, setThemeToDelete] = useState(null);

  const [data, setData] = useState(null);
  const [errors, setErrors] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const handleFetchThemes = useCallback(() => {
    dispatch(fetchThemeList(nodeId));
  }, [dispatch, nodeId]);

  const handleCloseThemesDialog = useCallback(() => {
    dispatch(hideThemeList());
  }, [dispatch]);

  const handleCloseThemeDialog = useCallback(() => {
    dispatch(hideTheme());
  }, [dispatch]);

  const handleCreateTheme = useCallback(() => {
    dispatch(showTheme());
  }, [dispatch]);

  const handleEditTheme = useCallback(
    (_, {__Guid, title}) => {
      dispatch(getTheme(nodeId, __Guid, title));
    },
    [dispatch, nodeId]
  );

  const handleFetchThemeSchema = useCallback(() => {
    dispatch(fetchThemeSchema(nodeId));
  }, [dispatch, nodeId]);

  const handleSubmitTheme = useCallback(() => {
    if ((errors || []).length > 0) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
      if (themeId) {
        dispatch(updateTheme(nodeId, themeId, data));
      } else {
        dispatch(createTheme(nodeId, data));
      }
    }
  }, [dispatch, errors, themeId, nodeId, data]);

  const handleStartDelete = useCallback((_, theme) => {
    setThemeToDelete(theme);
  }, []);

  const handleAbortDelete = useCallback(() => {
    setThemeToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    dispatch(deleteTheme(nodeId, themeToDelete.__Guid, themeToDelete.title));
    setThemeToDelete(null);
  }, [dispatch, nodeId, themeToDelete]);

  useEffect(() => {
    setData(theme);
    setErrors(null);
    setShowValidation(false);

    return () => {
      setData(null);
      setErrors(null);
      setShowValidation(false);
    };
  }, [theme]);

  if (!nodeId || !isVisible) {
    return null;
  }

  return (
    <SettingsDialog
      title={t("modules.packageManager.themesManager.dialog.title")}
      open={isVisible}
      onClose={handleCloseThemesDialog}
      className="themes-manager__themes-dialog"
    >
      <Call cb={handleFetchThemes} disabled={themes !== null}>
        <MaterialTable
          id={"themes-manager__table"}
          data={themes || []}
          rightActions={[
            {
              id: "themes-manager__table__actions__create",
              label: t("modules.packageManager.themesManager.actions.create.label"),
              startIcon: <AddIcon />,
              onClick: handleCreateTheme
            }
          ]}
          actions={[
            {
              icon: <EditIcon />,
              tooltip: t("modules.packageManager.themesManager.actions.edit.tooltip"),
              onClick: handleEditTheme,
              className: "themes-manager__table__actions__edit"
            },
            {
              icon: <DeleteIcon />,
              tooltip: t("modules.packageManager.themesManager.actions.delete.tooltip"),
              onClick: handleStartDelete,
              className: "themes-manager__table__actions__delete"
            }
          ]}
          columns={[
            {
              field: "__Guid",
              title: t("modules.packageManager.themesManager.columns.id.label"),
              hidden: true,
              getClassName: () => "themes-manager__table__columns__id"
            },
            {
              field: "title",
              title: t("modules.packageManager.themesManager.columns.title.label"),
              render: ({title}) => localizeI18nObj(title, language, languages),
              getClassName: () => "themes-manager__table__columns__title"
            },
            {
              field: "parent",
              title: t("modules.packageManager.themesManager.columns.parent.label"),
              render: ({parent}) => {
                const parentTheme = themes?.find(theme => theme.__Guid === parent);
                return localizeI18nObj(parentTheme?.title, language, languages);
              },
              getClassName: () => "themes-manager__table__columns__parent"
            }
          ]}
        />

        <ConfirmationDialog
          title={t("modules.packageManager.themesManager.deleteDialog.title")}
          content={t("modules.packageManager.themesManager.deleteDialog.description")}
          open={themeToDelete !== null}
          onClose={handleAbortDelete}
          onConfirm={handleConfirmDelete}
          className="themes-manager__delete-theme-dialog"
        />

        <FullscreenDialog
          open={theme !== null}
          title={
            themeId
              ? t("modules.packageManager.themesManager.editDialog.title")
              : t("modules.packageManager.themesManager.createDialog.title")
          }
          onClose={handleCloseThemeDialog}
          className="themes-manager__theme-dialog"
        >
          <DialogContent>
            <Call cb={handleFetchThemeSchema} disabled={schema !== null}>
              {schema && uiSchema && (
                <CustomJsonForms
                  schema={schema}
                  uischema={uiSchema}
                  data={data}
                  setData={setData}
                  errors={errors}
                  setErrors={setErrors}
                  showValidation={showValidation}
                  i18nPrefix={"modules.packageManager.themesManager"}
                />
              )}
            </Call>
          </DialogContent>
          <DialogActions>
            <Button className="themes-manager__theme-dialog__cancel-btn" onClick={handleCloseThemeDialog}>
              {t("commons.confirm.cancel")}
            </Button>
            <Button className="themes-manager__theme-dialog__submit-btn" onClick={handleSubmitTheme}>
              {t("commons.confirm.submit")}
            </Button>
          </DialogActions>
        </FullscreenDialog>
      </Call>
    </SettingsDialog>
  );
};

export default ThemesManager;
